'use client';

import React from 'react';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';
import { useFocusEffect } from '../../hooks';

/**
 * ProjectChip component
 * Displays a clickable chip with an icon and label
 * Used in the projects filter section
 * Enhanced with styling consistent with the rest of the portfolio
 */
export interface ProjectChipProps {
  label: string;
  iconName?: string;
  isActive?: boolean;
  onClick?: () => void;
  className?: string;
}

export function ProjectChip({
  label,
  iconName,
  isActive = false,
  onClick,
  className
}: ProjectChipProps) {
  // Use our custom focus effect hook
  const { isFocused, ref, focusProps } = useFocusEffect<HTMLButtonElement>();
  
  // Map icon names to Iconify icon IDs
  const getIconId = (name: string) => {
    const iconMap: Record<string, string> = {
      'FaTree': 'fa6-solid:tree',
      'FaEye': 'fa6-solid:eye',
      'IoMdPulse': 'ion:pulse',
      'RiSpeakLine': 'ri:speak-line',
      'FaHeadphones': 'fa6-solid:headphones',
      'FaMicrophone': 'fa6-solid:microphone'
    };
    
    return iconMap[name] || 'carbon:document';
  };
  
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-300',
        'border text-sm font-medium',
        isActive
          ? 'bg-slate-700 dark:bg-slate-600 text-white border-slate-700 dark:border-slate-600'
          : isFocused
            ? 'bg-slate-200/80 dark:bg-slate-700/50 text-slate-800 dark:text-slate-200 border-slate-300 dark:border-slate-600/50 shadow-md'
            : 'bg-slate-100 dark:bg-slate-800/50 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-700/50 hover:bg-slate-200/50 dark:hover:bg-slate-700/30',
        className
      )}
      {...focusProps}
    >
      {iconName && (
        <Icon 
          icon={getIconId(iconName)} 
          className={cn(
            "h-4 w-4 transition-transform duration-300",
            isFocused || isActive ? "scale-110" : "scale-100"
          )} 
        />
      )}
      <span className={cn(
        "transition-transform duration-300",
        isFocused && !isActive ? "translate-x-0.5" : ""
      )}>
        {label}
      </span>
    </button>
  );
} 